import { useActiveAddress, useConnection } from "@arweave-wallet-kit/react"
import { css } from "@linaria/core"
import { styled } from "@linaria/react"
import { Info } from "@mui/icons-material"
import {
  Box,
  Collapse,
  Dialog,
  Paper,
  Popover,
  Button as MuiButton,
  Stack,
  Theme,
  Typography,
  useMediaQuery,
  Link as MuiLink,
} from "@mui/material"
import { useQuery } from "@tanstack/react-query"
import { ArrowNarrowLeft, ChevronDown, Plus, RefreshCw01, XClose } from "@untitled-ui/icons-react"
import { enqueueSnackbar } from "notistack"
import { useContext, useEffect, useMemo, useRef, useState } from "react"

import { Link, useSearchParams } from "react-router-dom"

import { useAo } from "../ao/ao"
import {
  calculateProvideEarnings,
  OrderStatus,
  provide,
  useReserves,
  create,
  readProvideResult,
  usePendingProvide,
  partialProvide,
  cancelProvide,
} from "../ao/pool"
import { useSuggestedPool, useTokens } from "../ao/router"
import { getBalance, TokenInfo, useTotalSupply } from "../ao/token"
import Alert from "../components/Alert"
import Button from "../components/Button"
import { CardholderIcon } from "../components/CardholderIcon"
import { IconButton } from "../components/IconButton"
import { DEFAULT_SLIPPAGE, SettingsPopover } from "../components/SettingsPopover"
import Skeleton from "../components/Skeleton"
import { Subheading } from "../components/Subheading"
import TokenInput, { SmallButton } from "../components/TokenInput"
import { AddOrderDetails } from "../components/add/AddOrderDetails"
import { AddOrderPendingForm } from "../components/add/AddOrderPendingForm"
import { AddOrderPreviewForm } from "../components/add/AddOrderPreviewForm"
import { AddOrderSnackbar } from "../components/add/AddOrderSnackbar"
import { AddOrderSuccessForm } from "../components/add/AddOrderSuccessForm"
import { CreatePoolAnimation } from "../components/create-pool-anim/CreatePoolAnimation"
import { ConfigContext } from "../contexts/config"
import { useTokenInfo } from "../hooks/use-token-info"
import { REFRESH_INTERVAL } from "../settings"
import { NumberFont } from "../theme"
import {
  parseBigIntAsNumber,
  formatTicker,
  parseNumberAsBigInt,
  formatNumberAuto,
} from "../utils/format"
import { retryable } from "../utils/utils"

// Available fees TODO: GET FROM AMM_FACTORY
const availableFees = [25n, 75n, 125n, 300n]

export default function Add() {
  useEffect(() => {
    document.title = "Add Liquidity · Botega"
  }, [])

  // token selections
  const [searchParams, setSearchParams] = useSearchParams()
  const tokenAId = searchParams.get("tokenA") || undefined
  const tokenBId = searchParams.get("tokenB") || undefined
  const feePercentageBps = useMemo(() => {
    try {
      const x = BigInt(searchParams.get("fee") as string)
      if (!availableFees.includes(x)) return 25n
      return x
    } catch {
      return 25n
    }
  }, [searchParams])

  // if from and to tokens are the same, remove to token
  useEffect(() => {
    if (tokenAId && tokenBId && tokenAId === tokenBId) {
      searchParams.delete("tokenB")
      setSearchParams(searchParams, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenAId, tokenBId])

  // inputs
  const [tokenAValue, setTokenAValue] = useState<string>("")
  const [tokenBValue, setTokenBValue] = useState<string>("")

  // ref used in knowing how to update the ratios
  const tokenBInputRef = useRef<HTMLInputElement>(null)

  // token infos
  const [tokenA, loadingTokenA] = useTokenInfo(tokenAId)
  const [tokenB, loadingTokenB] = useTokenInfo(tokenBId)

  // all tokens
  const [tokens] = useTokens()

  const suggestedTokens = useMemo(() => {
    return tokens?.filter((token) => token.id !== tokenAId) || null
  }, [tokens, tokenAId])

  // find pool
  const [pool, poolLoading, refetchPool, isRefetching] = useSuggestedPool(
    tokenAId,
    tokenBId,
    feePercentageBps,
  )
  const poolId = pool?.poolId
  const isPoolPending = pool?.pending

  useEffect(() => {
    console.log("onPoolSelected", pool)
  }, [pool])

  // reserves
  const [reserves, loadingReserves, refetchReserves] = useReserves(poolId, tokenAId, tokenBId)

  const poolEmpty = useMemo(() => {
    if (!poolId || loadingReserves || poolLoading || !reserves || !tokenAId || !tokenBId)
      return false

    const isEmpty = reserves[tokenAId] === 0n || reserves[tokenBId] !== 0n

    console.log("onPoolEmptyUpdate", {
      reserves,
      isEmpty,
    })

    return isEmpty
  }, [poolId, loadingReserves, poolLoading, reserves, tokenAId, tokenBId])

  useEffect(() => {
    if (!poolId) return

    const interval = setInterval(() => {
      refetchReserves().then(({ data }) => {
        console.log("onReservesUpdate", data)
      })
    }, REFRESH_INTERVAL)

    return () => {
      clearInterval(interval)
    }
  }, [poolId, refetchReserves])

  useEffect(() => {
    console.log("onReservesUpdate", {
      reserves,
    })
  }, [reserves])

  // pool token
  const [lpToken] = useTokenInfo(poolId as string)

  // slippage tolerance percentage
  const [slippage, setSlippage] = useState(DEFAULT_SLIPPAGE)

  // ao instance
  const ao = useAo()

  // active address
  const activeAddr = useActiveAddress()

  // config
  const [config] = useContext(ConfigContext)

  // connect wallet
  const { connect } = useConnection()

  // normalized input values
  const tokenANormalized = useMemo<bigint>(() => {
    if (!tokenA) return 0n
    try {
      return parseNumberAsBigInt(tokenAValue, tokenA.denomination)
    } catch {
      return 0n
    }
  }, [tokenAValue, tokenA])
  const tokenBNormalized = useMemo<bigint>(() => {
    if (!tokenB) return 0n
    try {
      return parseNumberAsBigInt(tokenBValue, tokenB.denomination)
    } catch {
      return 0n
    }
  }, [tokenBValue, tokenB])

  // pool token total supply
  const [lpTotalSupply, loadingTotalSupply, refetchLpTotalSupply] = useTotalSupply(poolId as string)

  // balances
  const { data: tokenABalance, refetch: refetchTokenABalance } = useQuery({
    queryKey: [`balance_${tokenAId}`, activeAddr, config],
    enabled: !!activeAddr,
    queryFn: async () => {
      if (!activeAddr || !tokenAId) throw new Error("Params missing")

      return getBalance(tokenAId, activeAddr, ao).catch(() => 0n)
    },
  })
  const { data: tokenBBalance, refetch: refetchTokenBBalance } = useQuery({
    queryKey: [`balance_${tokenBId}`, activeAddr, config],
    enabled: !!activeAddr,
    queryFn: async () => {
      if (!activeAddr || !tokenBId) throw new Error("Params missing")

      return getBalance(tokenBId, activeAddr, ao).catch(() => 0n)
    },
  })
  const {
    data: lpBalance,
    isLoading: loadingLpBalance,
    refetch: refetchLpBalance,
  } = useQuery({
    queryKey: [`balance_${poolId}`, activeAddr, config, poolId],
    enabled: !!activeAddr && !!poolId,
    queryFn: async () => {
      if (!activeAddr || !poolId) throw new Error("No active address")

      return getBalance(poolId, activeAddr, ao).catch(() => 0n)
    },
  })

  const [oldLpBalance, setOldLpBalance] = useState<bigint>(0n)
  const [oldLpTotalSupply, setOldLpTotalSupply] = useState<bigint>(0n)

  // update ratios
  useEffect(() => {
    if (
      !tokenBInputRef.current ||
      !tokenAId ||
      !reserves?.[tokenAId] ||
      !tokenBId ||
      !reserves?.[tokenBId] ||
      loadingTokenA ||
      loadingTokenB
    )
      return

    if (document.activeElement === tokenBInputRef.current) {
      if (tokenBNormalized === 0n) {
        if (tokenANormalized === tokenBNormalized) return
        return setTokenAValue("0")
      }

      const targetValue = (tokenBNormalized * reserves[tokenAId]) / reserves[tokenBId]

      if (tokenANormalized !== targetValue) {
        setTokenAValue(parseBigIntAsNumber(targetValue, tokenA?.denomination, tokenA?.denomination))
      }
    } else {
      if (tokenANormalized === 0n) {
        if (tokenBNormalized === tokenANormalized) return
        return setTokenBValue("0")
      }

      const targetValue = (tokenANormalized * reserves[tokenBId]) / reserves[tokenAId]

      if (tokenBNormalized !== targetValue) {
        setTokenBValue(parseBigIntAsNumber(targetValue, tokenB?.denomination, tokenB?.denomination))
      }
    }
  }, [
    tokenANormalized,
    tokenBNormalized,
    tokenBInputRef,
    reserves,
    tokenAId,
    tokenBId,
    tokenA,
    tokenB,
    loadingTokenA,
    loadingTokenB,
  ])

  // details dropdown
  const [detailsDropdownOpen, setDetailsDropdownOpen] = useState(false)

  // prices
  const tokenAPrice = useMemo(() => {
    if (!tokenA || !tokenB || loadingReserves) return 0

    let reserveA, reserveB

    if (reserves === undefined || poolEmpty) {
      reserveA = tokenANormalized
      reserveB = tokenBNormalized
    } else {
      reserveA = reserves[tokenA.id]
      reserveB = reserves[tokenB.id]
    }

    if (reserveA === undefined || reserveB === undefined) return 0

    const base = Number(reserveB) * 10 ** tokenA.denomination
    const quote = Number(reserveA) * 10 ** tokenB.denomination
    const value = quote === 0 ? 0 : base / quote

    console.log("onTokenAPriceUpdate", {
      reserveA,
      reserveB,
      value,
      base,
      quote,
    })

    return value
  }, [tokenA, tokenB, loadingReserves, reserves, poolEmpty, tokenANormalized, tokenBNormalized])

  const tokenBPrice = useMemo(() => {
    if (!tokenA || !tokenB || loadingReserves) return 0

    let reserveA, reserveB

    if (reserves === undefined || poolEmpty) {
      reserveA = tokenANormalized
      reserveB = tokenBNormalized
    } else {
      reserveA = reserves[tokenA.id]
      reserveB = reserves[tokenB.id]
    }

    if (reserveA === undefined || reserveB === undefined) return 0

    const base = Number(reserveA) * 10 ** tokenB.denomination
    const quote = Number(reserveB) * 10 ** tokenA.denomination
    const value = quote === 0 ? 0 : base / quote

    console.log("onTokenAPriceUpdate", {
      reserveA,
      reserveB,
      value,
      base,
      quote,
    })

    return value
  }, [tokenA, tokenB, loadingReserves, reserves, poolEmpty, tokenANormalized, tokenBNormalized])

  // pool token earnings for this provide message
  const lpAmountExpected = useMemo(() => {
    if (loadingTotalSupply || loadingReserves) return 0n
    const value = calculateProvideEarnings({
      reserveA: tokenAId ? reserves?.[tokenAId] || 0n : 0n,
      qtyA: tokenANormalized || 0n,
      qtyB: tokenBNormalized || 0n,
      lpTotalSupply,
    })

    console.log("onAmountExpectedUpdate", {
      reserves,
      tokenANormalized,
      tokenBNormalized,
      lpTotalSupply,
      value,
    })

    return value
  }, [
    loadingTotalSupply,
    loadingReserves,
    reserves,
    tokenAId,
    tokenANormalized,
    tokenBNormalized,
    lpTotalSupply,
  ])

  // minimum received
  const receiveAtLeast = useMemo(() => {
    if (lpAmountExpected === 0n) return "0"

    const output = lpAmountExpected
    const minimumReceived = (output * BigInt((100 - slippage) * 1_000_000)) / 1_000_000n / 100n

    console.log("onSlippageCalculation", {
      output,
      slippage,
      minimumReceived,
    })

    return parseBigIntAsNumber(minimumReceived, lpToken?.denomination, lpToken?.denomination)
  }, [lpAmountExpected, slippage, lpToken])

  // pool share
  const currentPoolShare = useMemo(() => {
    if (
      loadingTotalSupply ||
      loadingLpBalance ||
      lpBalance === undefined ||
      lpTotalSupply === undefined
    )
      return null
    if (lpBalance === 0n) return 0n
    if (lpTotalSupply === 0n) return 0n

    const value = (lpBalance * 10000n) / lpTotalSupply
    console.log("onCurrentPoolShareUpdate", {
      lpTotalSupply,
      lpBalance,
      value,
    })

    return value
  }, [loadingTotalSupply, loadingLpBalance, lpBalance, lpTotalSupply])

  const newPoolShare = useMemo(() => {
    if (!loadingTotalSupply && !lpTotalSupply) return 10000n
    if (loadingTotalSupply || lpAmountExpected === 0n) return null

    const value =
      (((lpBalance || 0n) + lpAmountExpected) * 10000n) / (lpTotalSupply + lpAmountExpected)

    console.log("onNewPoolShareUpdate", {
      lpBalance,
      lpAmountExpected,
      lpTotalSupply,
      value,
    })

    return value
  }, [loadingTotalSupply, lpTotalSupply, lpAmountExpected, lpBalance])

  // button validation
  const validationError = useMemo(() => {
    if (isPoolPending) {
      return "This pool is being created. Hold tight!"
    }
    if (!tokenA) {
      return "Select a token"
    }
    if (!tokenB) {
      return "Select a token"
    }
    if (tokenAId === tokenBId) {
      return "Invalid pair"
    }
    if (!tokenANormalized || !tokenBNormalized) {
      return "Enter an amount"
    }
    if (typeof tokenABalance === "bigint" && tokenANormalized > tokenABalance) {
      return "Insufficient funds"
    }
    if (typeof tokenBBalance === "bigint" && tokenBNormalized > tokenBBalance) {
      return "Insufficient funds"
    }
    if (isRefetching) {
      return "Refreshing pool..."
    }

    return ""
  }, [
    isPoolPending,
    tokenA,
    tokenB,
    tokenAId,
    tokenBId,
    tokenANormalized,
    tokenBNormalized,
    tokenABalance,
    tokenBBalance,
    isRefetching,
  ])

  // confirm popover
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)

  function showPopover() {
    if (validationError) return
    setConfirmDialogOpen(true)
  }

  function closePopover() {
    if (pendingProvide?.provideFound) return
    if (orderStatus === "preparing") return

    if (orderStatus === "success" || orderStatus === "error" || orderStatus === "pending") {
      resetForm()
      enqueueSnackbar(
        <AddOrderSnackbar
          lpToken={lpToken}
          tokenA={tokenA as TokenInfo}
          tokenB={tokenB as TokenInfo}
          poolId={poolId as string}
          orderId={orderId as string}
          transferIds={transferIds}
          tokenAValue={tokenAValue}
          tokenBValue={tokenBValue}
          feePercentageBps={feePercentageBps}
          lpAmountExpected={lpAmountExpected}
          receiveAtLeast={receiveAtLeast}
          slippage={slippage}
          tokenAPrice={tokenAPrice}
          tokenBPrice={tokenBPrice}
          reserves={reserves}
          orderStatus={orderStatus}
          orderError={orderError}
          orderOutputAmount={orderOutputAmount}
          newPoolShare={newPoolShare}
          currentPoolShare={currentPoolShare}
          oldLpTotalSupply={oldLpTotalSupply}
          oldLpBalance={oldLpBalance}
        />,
        {
          persist: true,
          key: orderId,
        },
      )
    }

    resetState()
    setConfirmDialogOpen(false)
  }

  // order id
  const [orderId, setOrderId] = useState<string>()
  const [transferIds, setTransferIds] = useState<[string, string]>()

  // order status
  const [orderStatus, setOrderStatus] = useState<OrderStatus>()
  const [firstTxSigned, setFirstTxSigned] = useState(false)

  // order error
  const [orderError, setOrderError] = useState<string>()

  // provide result
  const [orderOutputAmount, setOrderOutputAmount] = useState<bigint>()
  const [orderConfirmationId, setOrderConfirmationId] = useState<string>()

  // Fee Popover
  const [feePopoverAnchorEl, setFeePopoverAnchorEl] = useState<HTMLButtonElement | null>(null)

  function handleOpenFeePopover(event: React.MouseEvent<HTMLButtonElement>) {
    setFeePopoverAnchorEl(event.currentTarget)
  }

  function handleCloseFeePopover() {
    setFeePopoverAnchorEl(null)
  }

  const feePopoverOpen = Boolean(feePopoverAnchorEl)
  const feePopoverId = feePopoverOpen ? "simple-fee-popover" : undefined

  // execute provide
  async function doProvide() {
    if (!!validationError) return

    let finalPoolId = poolId

    let firstTxAccepted = false
    try {
      // create pool if it doesn't exist
      if (!finalPoolId) {
        setOrderStatus("preparing")
        finalPoolId = await create(tokenAId as string, tokenBId as string, feePercentageBps, ao)
        console.log("onPoolCreation", finalPoolId)
        refetchPool()
      }

      setOrderStatus("pendingWalletConfirmation")
      const [firstTransfer, secondTransfer] = await provide(
        {
          tokenA: {
            token: tokenAId as string,
            quantity: tokenANormalized,
          },
          tokenB: {
            token: tokenBId as string,
            quantity: tokenBNormalized,
          },
          pool: finalPoolId,
          slippageTolerance: slippage,
        },
        ao,
        () => {
          setFirstTxSigned(true)
          firstTxAccepted = true
        },
      )
      console.log("onOrderId", [firstTransfer, secondTransfer])
      setOrderId(secondTransfer) // Keep backward compatibility for orderId
      setTransferIds([firstTransfer, secondTransfer])
      setOldLpBalance(lpBalance || 0n)
      setOldLpTotalSupply(lpTotalSupply || 0n)
      setOrderStatus("pending")

      await Promise.all([
        refetchTokenBBalance(),
        refetchTokenABalance(),
        refetchLpBalance(),
        refetchLpTotalSupply(),
      ])
    } catch (error) {
      if (!(error instanceof Error)) {
        setOrderError(String(error))
        setOrderStatus("error")
        return
      }

      if (!error.message.includes("User rejected")) {
        setOrderStatus("error")
        setOrderError(error.message)
        return
      }

      console.log("onUserRejected", firstTxAccepted)

      if (firstTxAccepted) {
        console.log("onSecondTxRejected")
        const result = await refetchPendingProvide()
        console.log("onSecondTxRejected", result)
      }

      setOrderStatus(undefined)
    }
  }

  // pending provide
  const [pendingProvide, , refetchPendingProvide] = usePendingProvide(poolId)

  useEffect(() => {
    if (!transferIds || !poolId) return

    const abortController = new AbortController()
      ; (async function () {
        try {
          const [lpTokenReceived, confirmationId] = await retryable(readProvideResult)(transferIds[0], transferIds[1])
          await refetchPendingProvide()

          if (abortController.signal.aborted) return

          setOrderOutputAmount(lpTokenReceived)
          setOrderConfirmationId(confirmationId)
          setOrderStatus("success")
        } catch (error) {
          if (abortController.signal.aborted) return

          if (!(error instanceof Error)) {
            setOrderError(String(error))
            setOrderStatus("error")
            return
          }

          setOrderStatus("error")
          setOrderError(error.message)
        }
      })()

    return () => {
      abortController.abort()
    }
  }, [transferIds, poolId, refetchPendingProvide])

  // reset inputs
  function resetForm() {
    setTokenAValue("")
    setTokenBValue("")
  }

  // reset order state
  function resetState() {
    setOrderOutputAmount(undefined)
    setOrderStatus(undefined)
    setOrderError(undefined)
    setOrderId(undefined)
    setTransferIds(undefined)
    setFirstTxSigned(false)
  }

  useEffect(() => {
    console.log("onPendingProvideUpdate", {
      pendingProvide,
    })
  }, [pendingProvide])

  useEffect(() => {
    if (!pendingProvide?.provideFound || !tokenA || !tokenB) return

    setConfirmDialogOpen(true)

    if (pendingProvide?.token === tokenAId) {
      tokenBInputRef.current?.blur()
      setTokenAValue(
        parseBigIntAsNumber(pendingProvide?.quantity, tokenA?.denomination, tokenA?.denomination),
      )
    } else {
      tokenBInputRef.current?.focus()
      setTokenBValue(
        parseBigIntAsNumber(pendingProvide?.quantity, tokenB?.denomination, tokenB?.denomination),
      )
    }
  }, [pendingProvide, tokenA, tokenB])

  const recoverState = useMemo(() => pendingProvide?.provideFound, [pendingProvide])

  async function withdrawPending() {
    if (!pendingProvide) return

    try {
      setOrderStatus("pendingWalletConfirmation")
      const orderId = await cancelProvide(pendingProvide, ao)
      console.log("onCancelProvideId", orderId)
      await refetchPendingProvide()

      resetState()
      setConfirmDialogOpen(false)
      refetchTokenBBalance()
      refetchTokenABalance()
    } catch (error) {
      if (!(error instanceof Error)) {
        setOrderError(String(error))
        setOrderStatus("error")
        return
      }

      if (error.message.includes("User rejected")) {
        setOrderStatus(undefined)
        return
      }

      setOrderStatus("error")
      setOrderError(error.message)
    }
  }

  async function resumeProvide() {
    if (!!validationError || !pendingProvide) return

    try {
      setOrderStatus("pendingWalletConfirmation")
      const orderId = await partialProvide(
        {
          token: pendingProvide?.token === tokenAId ? (tokenBId as string) : (tokenAId as string),
          quantity: pendingProvide?.token === tokenAId ? tokenBNormalized : tokenANormalized,
          pool: poolId as string,
          slippageTolerance: slippage,
        },
        ao,
      )
      console.log("onOrderId", orderId)
      setOrderId(orderId)
      setOrderStatus("pending")

      refetchPendingProvide()
      refetchTokenBBalance()
      refetchTokenABalance()
    } catch (error) {
      if (!(error instanceof Error)) {
        setOrderError(String(error))
        setOrderStatus("error")
        return
      }

      if (error.message.includes("User rejected")) {
        setOrderStatus(undefined)
        return
      }

      setOrderStatus("error")
      setOrderError(error.message)
    }
  }

  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"))

  return (
    <Page style={{ position: "relative" }}>
      <Toolbar
        className={css`
          background: var(--mui-palette-titleBar);
          border-bottom: 1px solid var(--mui-palette-titleBarBorder);
          height: 34px;

          border-top-left-radius: var(--mui-shape-borderRadius);
          border-top-right-radius: var(--mui-shape-borderRadius);
        `}
      >
        <Link to="/pools">
          <IconButton size="medium">
            <ArrowNarrowLeft />
          </IconButton>
        </Link>
        <Subheading>Add Liquidity</Subheading>
        <Stack direction="row" gap={0.5} alignItems="center">
          {poolId && (
            <IconButton
              size="medium"
              disabled={loadingReserves}
              className={css`
                svg {
                  width: 16px;
                  height: 16px;
                }
              `}
              onClick={() => {
                if (!poolId) return

                refetchReserves().then(({ data }) => {
                  console.log("onReservesUpdate", data)
                })
              }}
            >
              {" "}
              <RefreshCw01
                style={{
                  animation: loadingReserves ? "loading-rotate 0.57s ease-in-out infinite" : "none",
                }}
              />
            </IconButton>
          )}
          <SettingsPopover slippage={slippage} setSlippage={setSlippage} />
        </Stack>
      </Toolbar>
      <Stack
        gap={1}
        component={Paper}
        elevation={0}
        sx={{
          padding: 1,
          background: "var(--mui-palette-background-paper)",
          borderTopLeftRadius: "0",
          borderTopRightRadius: "0",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <Inputs>
          <Box
            sx={{
              marginTop: 1,
              marginX: 1,
              height: 25,
            }}
          >
            {poolLoading ? (
              <Skeleton height="100%" />
            ) : (
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 0.5,
                  display: "flex",
                  justifyContent: "center",
                  padding: 0.25,
                }}
              >
                <Typography variant="caption">
                  {poolId ? (
                    <>
                      Adding to an <b>Existing Pool</b>
                    </>
                  ) : (
                    <>
                      Creating a <b>New Pool</b>
                    </>
                  )}
                </Typography>
              </Paper>
            )}
          </Box>
          <Box
            sx={(theme) => ({
              background: "var(--mui-palette-background-default)",
              borderRadius: "var(--mui-shape-borderRadius)",
              [theme.breakpoints.up("md")]: {
                minWidth: "unset",
                display: "grid",
                gridTemplateColumns: "1fr auto 1fr",
                alignItems: "flex-start",
              },
              [theme.breakpoints.down("md")]: {
                display: "flex",
                flexDirection: "column",
              },
            })}
          >
            <TokenInput
              tokenID={tokenAId}
              tokenInfo={tokenA}
              onTokenIdChange={(tokenId) => {
                if (!tokenId) searchParams.delete("tokenA")
                else searchParams.set("tokenA", tokenId)
                setSearchParams(searchParams, { replace: true })
              }}
              tokens={tokens}
              autoFocus
              onSubmit={showPopover}
              value={tokenAValue}
              onChange={(newValue, useMax) => {
                console.log("onInputChange", {
                  tokenId: tokenAId,
                  amount: newValue,
                  useMax,
                })
                setTokenAValue(newValue)
              }}
              balance={tokenABalance}
            />
            <Box
              sx={(theme) => ({
                display: "flex",
                height: "100%",
                width: "100%",
                position: "relative",
                alignItems: "center",
                justifyContent: "center",
                "& > button": {
                  [theme.breakpoints.down("md")]: {
                    transform: "rotate(90deg) !important",
                  },
                },
              })}
            >
              <Box
                sx={(theme) => ({
                  backgroundColor: "var(--mui-palette-borderPrimary)",
                  height: "100%",
                  width: "1px",
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  [theme.breakpoints.down("md")]: {
                    width: "100%",
                    height: "1px",
                  },
                })}
              />
              <IconButton
                className={css`
                  background-color: var(--mui-palette-background-default);
                `}
                variant="outlined"
                size="medium"
                color="accent"
                disabled
              >
                <Plus />
              </IconButton>
            </Box>
            <TokenInput
              tokenID={tokenBId}
              onTokenIdChange={(tokenId) => {
                if (!tokenId) searchParams.delete("tokenB")
                else searchParams.set("tokenB", tokenId)
                setSearchParams(searchParams, { replace: true })
              }}
              tokenInfo={tokenB}
              tokens={suggestedTokens}
              onSubmit={showPopover}
              ref={tokenBInputRef}
              value={tokenBValue}
              onChange={(newValue, useMax) => {
                console.log("onInputChange", {
                  tokenId: tokenBId,
                  amount: newValue,
                  useMax,
                })

                tokenBInputRef.current?.focus()
                setTokenBValue(newValue)
              }}
              balance={tokenBBalance}
            />
          </Box>
        </Inputs>
        <Stack
          gap={1}
          direction="row"
          justifyContent="space-between"
          sx={{
            paddingX: 1,
          }}
          alignItems="center"
        >
          <MuiButton
            variant="text"
            sx={{
              color: "var(--mui-palette-text-secondary)",
              "&:hover": {
                background: "none",
              },
              padding: 0,
            }}
            aria-describedby={feePopoverId}
            onClick={handleOpenFeePopover}
            startIcon={<Info />}
          >
            Pool Fee
          </MuiButton>
          <Popover
            open={feePopoverOpen}
            onClose={handleCloseFeePopover}
            anchorEl={feePopoverAnchorEl}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              Set the percentage fee your liquidity pool charges per transaction.
              <br />
              Higher fees mean greater returns for LPs but higher costs for traders.
              <br />
              <MuiLink
                href="https://docs.autonomous.finance/products/platforms/botega"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: "var(--mui-palette-secondary-main)",
                }}
              >
                Learn more
              </MuiLink>
            </Typography>
          </Popover>
          <Stack
            direction="row"
            sx={{
              marginBottom: -0.25,
              "& button": {
                borderRadius: "3px !important",
              },
              "& button:not(.active)": {
                background: "none",
              },
            }}
          >
            {availableFees.map((fee) => (
              <SmallButton
                className={feePercentageBps === fee ? "active" : ""}
                onClick={() => {
                  searchParams.set("fee", fee.toString())
                  setSearchParams(searchParams, { replace: true })
                }}
              >
                {Number(fee) / 100}%
              </SmallButton>
            ))}
          </Stack>
        </Stack>
        <Box
          sx={(theme) => ({
            borderRadius: 1,
            paddingY: 1,
            paddingX: 1.5,
            background: "var(--mui-palette-background-default)",
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 2,
            [theme.breakpoints.down("md")]: {
              display: "flex",
              flexDirection: "column",
            },
          })}
        >
          {tokenA && tokenB && !loadingReserves && !poolLoading ? (
            <>
              <Stack>
                <Typography fontFamily={NumberFont} color="accent.main">
                  {formatNumberAuto(tokenAPrice)}
                </Typography>
                <span>
                  {formatTicker(tokenB.ticker, true)} per {formatTicker(tokenA.ticker, true)}
                </span>
              </Stack>
              <Stack>
                <Typography fontFamily={NumberFont} color="accent.main">
                  {formatNumberAuto(tokenBPrice)}
                </Typography>
                <span>
                  {formatTicker(tokenA.ticker, true)} per {formatTicker(tokenB.ticker, true)}
                </span>
              </Stack>
              <Stack
                sx={{
                  display: poolId && !poolEmpty ? "block" : "none",
                }}
              >
                {currentPoolShare === null ? (
                  <Skeleton width="60px" height="24px" />
                ) : (
                  <Typography fontFamily={NumberFont} color="accent.main">
                    {(Number(currentPoolShare) / 100).toFixed(2)}%
                  </Typography>
                )}
                <span>Current Share of Pool</span>
              </Stack>
            </>
          ) : (
            <>
              <Stack gap={1}>
                <Skeleton width="60px" />
                <Skeleton width="80px" />
              </Stack>
              <Stack gap={1}>
                <Skeleton width="60px" />
                <Skeleton width="80px" />
              </Stack>
              <Stack gap={1}>
                <Skeleton width="60px" />
                <Skeleton width="80px" />
              </Stack>
            </>
          )}
        </Box>
        <Dialog
          fullScreen={isMobile}
          fullWidth
          maxWidth="md"
          position="top"
          sx={{
            "& .MuiDialog-paper": {
              minHeight: 450,
              justifyContent: "center",
            },
          }}
          open={confirmDialogOpen}
          onClose={closePopover}
        >
          {!recoverState && (
            <IconButton
              onClick={closePopover}
              size="large"
              style={{
                position: "absolute",
                top: "20px",
                right: "20px",
                zIndex: 1,
              }}
            >
              <XClose />
            </IconButton>
          )}
          {orderStatus === "preparing" ? (
            <Stack alignItems="center">
              <CreatePoolAnimation />
            </Stack>
          ) : orderStatus === "pending" ? (
            <AddOrderPendingForm
              tokenA={tokenA as TokenInfo}
              tokenB={tokenB as TokenInfo}
              tokenAValue={tokenAValue}
              tokenBValue={tokenBValue}
            />
          ) : orderStatus === "success" ? (
            <AddOrderSuccessForm
              lpToken={lpToken}
              tokenA={tokenA as TokenInfo}
              tokenB={tokenB as TokenInfo}
              tokenAValue={tokenAValue}
              tokenBValue={tokenBValue}
              poolId={poolId as string}
              orderConfirmationId={orderConfirmationId as string}
              orderOutputAmount={orderOutputAmount as bigint}
              oldLpTotalSupply={oldLpTotalSupply}
              oldLpBalance={oldLpBalance}
            />
          ) : recoverState ? (
            <Stack gap={2}>
              <AddOrderPreviewForm
                tokenA={tokenA as TokenInfo}
                tokenB={tokenB as TokenInfo}
                tokenAValue={tokenAValue}
                tokenBValue={tokenBValue}
              />
              <div>
                <AddOrderDetails
                  currentPoolShare={currentPoolShare}
                  newPoolShare={newPoolShare}
                  feePercentageBps={feePercentageBps}
                  lpAmountExpected={lpAmountExpected}
                  lpToken={lpToken}
                  receiveAtLeast={receiveAtLeast}
                  slippage={slippage}
                  tokenA={tokenA as TokenInfo}
                  tokenB={tokenB as TokenInfo}
                  tokenAPrice={tokenAPrice}
                  tokenBPrice={tokenBPrice}
                  reserves={reserves}
                />
              </div>
              <Alert type="warning">
                You have an incomplete order. Please complete or cancel your order before
                continuing.
              </Alert>
              <Stack direction="row" gap={2}>
                {pendingProvide?.token === tokenAId ? (
                  <>
                    <ContinueButton
                      onClick={withdrawPending}
                      className={
                        orderStatus === "pendingWalletConfirmation" ? "secondary" : "outlined"
                      }
                      disabled={orderStatus === "pendingWalletConfirmation"}
                    >
                      Withdraw {formatTicker(tokenA?.ticker)}
                    </ContinueButton>
                    <ContinueButton
                      onClick={resumeProvide}
                      className={
                        orderStatus === "pendingWalletConfirmation" ? "secondary" : "accent"
                      }
                      disabled={
                        orderStatus === "pendingWalletConfirmation" ||
                        tokenANormalized === 0n ||
                        tokenBNormalized === 0n
                      }
                    >
                      Supply {formatTicker(tokenB?.ticker)}
                    </ContinueButton>
                  </>
                ) : (
                  <>
                    <ContinueButton
                      onClick={resumeProvide}
                      className={
                        orderStatus === "pendingWalletConfirmation" ? "secondary" : "accent"
                      }
                      disabled={
                        orderStatus === "pendingWalletConfirmation" ||
                        tokenANormalized === 0n ||
                        tokenBNormalized === 0n
                      }
                    >
                      Supply {formatTicker(tokenA?.ticker)}
                    </ContinueButton>
                    <ContinueButton
                      onClick={withdrawPending}
                      className={
                        orderStatus === "pendingWalletConfirmation" ? "secondary" : "outlined"
                      }
                      disabled={orderStatus === "pendingWalletConfirmation"}
                    >
                      Withdraw {formatTicker(tokenB?.ticker)}
                    </ContinueButton>
                  </>
                )}
              </Stack>
            </Stack>
          ) : (
            <Stack gap={2}>
              <AddOrderPreviewForm
                tokenA={tokenA as TokenInfo}
                tokenB={tokenB as TokenInfo}
                tokenAValue={tokenAValue}
                tokenBValue={tokenBValue}
              />
              <div>
                <AddOrderDetails
                  currentPoolShare={currentPoolShare}
                  newPoolShare={newPoolShare}
                  feePercentageBps={feePercentageBps}
                  lpAmountExpected={lpAmountExpected}
                  lpToken={lpToken}
                  receiveAtLeast={receiveAtLeast}
                  slippage={slippage}
                  tokenA={tokenA as TokenInfo}
                  tokenB={tokenB as TokenInfo}
                  tokenAPrice={tokenAPrice}
                  tokenBPrice={tokenBPrice}
                  reserves={reserves}
                />
              </div>
              {orderError && (
                <Alert type="error" topSpace>
                  {orderError}
                </Alert>
              )}
              {!orderStatus && (
                <ContinueButton onClick={doProvide} className="accent">
                  {poolId
                    ? `Add to ${formatTicker(tokenA?.ticker, true)} - ${formatTicker(tokenB?.ticker, true)} Pool`
                    : `Create ${formatTicker(tokenA?.ticker, true)} - ${formatTicker(tokenB?.ticker, true)} Pool`}
                </ContinueButton>
              )}
              {orderStatus === "pendingWalletConfirmation" && !!poolId && (
                <ContinueButton disabled className="secondary">
                  Confirm {firstTxSigned ? "second" : "first"} transaction in your Wallet
                </ContinueButton>
              )}
              {orderStatus === "error" && (
                <ContinueButton onClick={closePopover} className="secondary">
                  Close
                </ContinueButton>
              )}
            </Stack>
          )}
        </Dialog>
        {!activeAddr ? (
          <ContinueButton onClick={connect} className="accent">
            Connect Wallet
            <CardholderIcon />
          </ContinueButton>
        ) : (
          <ContinueButton
            onClick={showPopover}
            disabled={!!validationError}
            className={!validationError ? "accent" : "secondary"}
          >
            {validationError ||
              (poolId ? "Add To Existing Liquidity Pool" : "Create New Liquidity Pool")}
          </ContinueButton>
        )}
      </Stack>
      {lpAmountExpected !== 0n && (
        <>
          <Toolbar
            className={css`
              margin-top: calc(var(--spacing) * 1.25);
            `}
          >
            <PriceLabel>
              {(Number(newPoolShare) / 100).toFixed(2)}% Your New Total Share of Pool
            </PriceLabel>
            <div />
            <IconButton
              size="medium"
              onClick={() => {
                setDetailsDropdownOpen((prev) => !prev)
              }}
              style={{
                transform: detailsDropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
              }}
            >
              <ChevronDown />
            </IconButton>
          </Toolbar>
          <Details>
            <Collapse in={detailsDropdownOpen}>
              <AddOrderDetails
                currentPoolShare={null}
                newPoolShare={null}
                feePercentageBps={feePercentageBps}
                lpAmountExpected={lpAmountExpected}
                lpToken={lpToken}
                receiveAtLeast={receiveAtLeast}
                slippage={slippage}
                tokenA={tokenA as TokenInfo}
                tokenB={tokenB as TokenInfo}
                tokenAPrice={tokenAPrice}
                tokenBPrice={tokenBPrice}
                reserves={reserves}
              />
            </Collapse>
          </Details>
        </>
      )}
    </Page>
  )
}

const PriceLabel = styled(Button)`
  padding: 0;
  font-weight: 700;
  text-align: start;
`

const Details = styled(Stack)`
  gap: 0;
  width: 100%;
  padding: 0 calc(var(--spacing) * 1.25);
  box-sizing: border-box;
`

const Toolbar = styled.div`
  width: 100%;
  padding: 0 calc(var(--spacing));
  box-sizing: border-box;

  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  justify-items: center;

  & > *:nth-child(1) {
    justify-self: start;
  }
  & > *:nth-child(3) {
    justify-self: end;
  }
`

const Page = styled.div`
  max-width: 900px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`

const Inputs = styled.section`
  background-color: var(--mui-palette-background-default);
  border-radius: var(--mui-shape-borderRadius);
`

const ContinueButton = styled(Button)`
  padding: 1.4rem 1.55rem;
  width: 100%;
  font-size: 1.35rem;
`
